var __assign = (this && this.__assign) || function () {
    __assign = Object.assign || function(t) {
        for (var s, i = 1, n = arguments.length; i < n; i++) {
            s = arguments[i];
            for (var p in s) if (Object.prototype.hasOwnProperty.call(s, p))
                t[p] = s[p];
        }
        return t;
    };
    return __assign.apply(this, arguments);
};
var __read = (this && this.__read) || function (o, n) {
    var m = typeof Symbol === "function" && o[Symbol.iterator];
    if (!m) return o;
    var i = m.call(o), r, ar = [], e;
    try {
        while ((n === void 0 || n-- > 0) && !(r = i.next()).done) ar.push(r.value);
    }
    catch (error) { e = { error: error }; }
    finally {
        try {
            if (r && !r.done && (m = i["return"])) m.call(i);
        }
        finally { if (e) throw e.error; }
    }
    return ar;
};
import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { forwardRef, useCallback, useEffect, useImperativeHandle, useRef, useState } from "react";
import { styled } from "@mui/system";
import { Box, Card, CardActionArea, CardContent, CardMedia, Grid, IconButton, Skeleton, Tooltip, Typography, } from "@mui/material";
import { useOnWindowResize } from "../../hooks/useOnWindowResize";
import { getTimeElapsedInStopWatchFormatString } from "../../helpers/GetTimeElapsedFormatted.helper";
import PlayCircleFilledRoundedIcon from "@mui/icons-material/PlayCircleFilledRounded";
import WaveSurfer from "wavesurfer.js";
import textCutOffAddElipsis from "../../utils/textCutOffAddElipsis";
import VTTextField from "../VTTextField";
import SaveAsRoundedIcon from "@mui/icons-material/SaveAsRounded";
import StopCircleRoundedIcon from "@mui/icons-material/StopCircleRounded";
import MoreHorizIcon from "@mui/icons-material/MoreHoriz";
import VTMenu from "../VTMenu";
import { v4 as uuid } from "uuid";
var StyledCard = styled(Card)(function () { return ({
    borderRadius: 15,
    maxWidth: "400px",
}); });
export default forwardRef(function (props, ref) {
    var sx = props.sx;
    var rootRef = useRef(null);
    var vtMenuExposedMethodsRef = useRef(null);
    useImperativeHandle(ref, function () { return ({
        getRootNode: function () {
            return rootRef.current;
        },
        closePopUpOptionsMenu: function () {
            var _a;
            (_a = vtMenuExposedMethodsRef.current) === null || _a === void 0 ? void 0 : _a.closePopUpMenu();
        },
    }); });
    return (_jsx(StyledCard, __assign({ ref: rootRef, elevation: 5, sx: sx }, { children: _jsx(CardContentComponentWrapper, __assign({ ref: vtMenuExposedMethodsRef }, props)) })));
});
var RecordingAndPlaybackState;
(function (RecordingAndPlaybackState) {
    RecordingAndPlaybackState[RecordingAndPlaybackState["PLAYBACK"] = 0] = "PLAYBACK";
    RecordingAndPlaybackState[RecordingAndPlaybackState["STOP"] = 1] = "STOP";
})(RecordingAndPlaybackState || (RecordingAndPlaybackState = {}));
// Card content for the visuals and layout of the VTCard
var CardContentComponentWrapper = forwardRef(function (_a, ref) {
    var sx = _a.sx, loading = _a.loading, audioSrc = _a.audioSrc, onTitleEdited = _a.onTitleEdited, autoSelectTextOnEdit = _a.autoSelectTextOnEdit, _b = _a.titleCutOffPoint, titleCutOffPoint = _b === void 0 ? 80 : _b, title = _a.title, menuOptions = _a.menuOptions;
    var waveFormContainerId = useRef("waveform-".concat(uuid()));
    var currentPlaybackTimeId = useRef("currentPlaybackTime-".concat(uuid()));
    var _c = __read(useOnWindowResize(), 2), windowWidth = _c[0], _ = _c[1];
    var _d = __read(useState(null), 2), cardMediaNode = _d[0], setCardMediaNode = _d[1];
    var CardMediaRef = useCallback(function (node) {
        setCardMediaNode(node);
    }, []);
    var _e = __read(useState(150 - 80), 2), cardMediaHeight = _e[0], setCardMediaHeight = _e[1];
    var _f = __read(useState(), 2), waveSurferObj = _f[0], setWaveSurferObj = _f[1];
    var waveSurferObjInitialSet = useRef(false);
    useEffect(function () {
        if (cardMediaNode) {
            var width = cardMediaNode.clientWidth;
            if (185 > width) {
                setCardMediaHeight(150 - 50);
            }
            else if (250 > width) {
                setCardMediaHeight(200 - 80);
            }
            else if (350 > width) {
                setCardMediaHeight(240 - 80);
            }
            else {
                setCardMediaHeight(280 - 90);
            }
        }
    }, [cardMediaNode, sx, windowWidth]);
    // useEffect(() => {
    //     if (waveSurferObjInitialSet.current === true) {
    //     }
    // }, [cardMediaHeight]);
    var timeElapsedBoxRef = useRef();
    var animationFrameRequestIdTimeElapsed = useRef(0);
    var _g = __read(useState(RecordingAndPlaybackState.PLAYBACK), 2), recordingPlaybackState = _g[0], setRecordingPlaybackState = _g[1];
    var audioRecorderManager = useRef(null);
    var _h = __read(useState(false), 2), titleInEdit = _h[0], setTitleInEdit = _h[1];
    var _j = __read(useState(""), 2), editedTitleValue = _j[0], setEditedTitleValue = _j[1];
    var requestAnimFrameIdRef = useRef(0);
    useEffect(function () {
        // when component unmounts stop/cancel any remaining animation frame
        return function () {
            window.cancelAnimationFrame(requestAnimFrameIdRef.current);
        };
    }, []);
    var _k = __read(useState(0), 2), audioClipDurationInMilliseconds = _k[0], setAudioClipDurationInMilliseconds = _k[1];
    useEffect(function () {
        if (waveSurferObj) {
            waveSurferObj.on("interaction", function () {
                waveSurferObj === null || waveSurferObj === void 0 ? void 0 : waveSurferObj.play();
                requestAnimFrameIdRef.current = window.requestAnimationFrame(step);
                setRecordingPlaybackState(RecordingAndPlaybackState.STOP);
            });
            var tryAndSetAudioClipDuration_1 = function () {
                if (!(waveSurferObj.getDuration() > 0) || isNaN(waveSurferObj.getDuration())) {
                    // try again
                    window.setTimeout(tryAndSetAudioClipDuration_1, 100);
                }
                else {
                    setAudioClipDurationInMilliseconds(waveSurferObj.getDuration() * 1000);
                }
            };
            window.setTimeout(tryAndSetAudioClipDuration_1, 100);
        }
    }, [waveSurferObj]);
    var currentAudioPlaybackTimeInMilliseconsRef = useRef(0);
    function step(timeStamp) {
        if (waveSurferObj.isPlaying()) {
            currentAudioPlaybackTimeInMilliseconsRef.current = waveSurferObj.getCurrentTime();
            document.getElementById(currentPlaybackTimeId.current).innerHTML = "".concat(getTimeElapsedInStopWatchFormatString(currentAudioPlaybackTimeInMilliseconsRef.current * 1000));
            requestAnimFrameIdRef.current = window.requestAnimationFrame(step);
        }
        else {
            window.cancelAnimationFrame(currentAudioPlaybackTimeInMilliseconsRef.current);
            document.getElementById(currentPlaybackTimeId.current).innerHTML =
                getTimeElapsedInStopWatchFormatString(0);
            setRecordingPlaybackState(RecordingAndPlaybackState.PLAYBACK);
        }
    }
    function setWaveSurferObjState(container) {
        setWaveSurferObj(WaveSurfer.create({
            container: container,
            waveColor: "#8426df",
            barWidth: 5,
            barHeight: 5,
            barRadius: 10,
            url: audioSrc,
            width: container === null || container === void 0 ? void 0 : container.clientWidth,
            height: container === null || container === void 0 ? void 0 : container.clientHeight,
        }));
    }
    var resizeObserverRef = useRef(null);
    var resizingDelayTimer = useRef(undefined);
    useEffect(function () {
        var waveFormContainer = document.getElementById(waveFormContainerId.current);
        if (!loading) {
            var resizeObserver = new ResizeObserver(function () {
                window.clearTimeout(resizingDelayTimer.current);
                resizingDelayTimer.current = window.setTimeout(function () {
                    // check if the observed div is still mounted
                    // else this will cause memory leak
                    if (waveFormContainer) {
                        waveFormContainer.innerHTML = "";
                        setWaveSurferObjState(waveFormContainer);
                    }
                }, 100);
            });
            resizeObserver.observe(waveFormContainer);
        }
    }, [loading]);
    // useEffect(() => {
    //     // if(waveSurferObj){
    //     //     waveSurferObj.destroy();
    //     // }
    // }, [loading]);
    var textFieldRef = useRef(null);
    useEffect(function () {
        if (titleInEdit && autoSelectTextOnEdit) {
            window.setTimeout(function () {
                // @ts-ignore
                console.log("ref", textFieldRef.current.firstChild.children[1].select());
            }, 100);
        }
    }, [titleInEdit]);
    var buttons;
    if (recordingPlaybackState === RecordingAndPlaybackState.PLAYBACK) {
        buttons = (_jsx(IconButton, __assign({ size: "small", sx: {}, onClick: function (e) {
                e.stopPropagation();
                waveSurferObj === null || waveSurferObj === void 0 ? void 0 : waveSurferObj.play();
                setRecordingPlaybackState(RecordingAndPlaybackState.STOP);
                requestAnimFrameIdRef.current = window.requestAnimationFrame(step);
            } }, { children: _jsx(PlayCircleFilledRoundedIcon, {}) })));
    }
    if (recordingPlaybackState === RecordingAndPlaybackState.STOP) {
        buttons = (_jsx(IconButton, __assign({ size: "small", sx: {}, onClick: function (e) {
                waveSurferObj === null || waveSurferObj === void 0 ? void 0 : waveSurferObj.stop();
                setRecordingPlaybackState(RecordingAndPlaybackState.PLAYBACK);
                window.cancelAnimationFrame(requestAnimFrameIdRef.current);
                document.getElementById(currentPlaybackTimeId.current).innerHTML =
                    getTimeElapsedInStopWatchFormatString(0);
            } }, { children: _jsx(StopCircleRoundedIcon, {}) })));
    }
    return (_jsxs(_Fragment, { children: [_jsx(CardActionArea, __assign({ disabled: loading }, { children: _jsxs(CardMedia, __assign({ onClick: function () { }, ref: CardMediaRef, sx: {
                        height: "".concat(cardMediaHeight, "px"),
                        position: "relative",
                    } }, { children: [menuOptions && !loading && (_jsx(VTMenu, __assign({ ref: ref, sx: {
                                width: "45px",
                                height: "45px",
                                position: "absolute",
                                top: "8px",
                                right: "8px",
                                zIndex: 99,
                                "& .MuiButtonBase-root": {
                                    "&:hover": { background: "rgb(0, 0, 0, 0.1)" },
                                },
                            }, type: "icon", color: "secondary", popOutMenuZIndex: menuOptions.menuPopOutZIndex, menuOptions: menuOptions.menuOptionsList, direction: "DOWN" }, { children: _jsx(MoreHorizIcon, { sx: { color: "white" } }) }))), !loading ? (_jsxs(Box, __assign({ component: "div", sx: {
                                display: "flex",
                                flexDirection: "column",
                                height: "100%",
                                width: "100%",
                            } }, { children: [_jsx(Box, { component: "div", id: waveFormContainerId.current, sx: {
                                        flex: 6,
                                        background: "#333",
                                        display: "flex",
                                    } }), _jsxs(Box, __assign({ ref: timeElapsedBoxRef, component: "div", onMouseDown: function (e) {
                                        e.stopPropagation();
                                    }, sx: {
                                        flex: 1,
                                        background: "#444",
                                        display: "flex",
                                        justifyContent: "space-between",
                                        alignItems: "center",
                                        fontFamily: "Digit",
                                        fontSize: "1.2em",
                                        position: "relative",
                                        padding: "5px",
                                        paddingLeft: "25px",
                                        paddingRight: "25px",
                                        cursor: "default",
                                    } }, { children: [
                                        // @ts-ignore
                                        buttons, _jsxs(Box, __assign({ component: "div", sx: { display: "flex", alignItems: "center" } }, { children: [_jsx(Box, __assign({ id: currentPlaybackTimeId.current, component: "div", sx: { display: "inline" } }, { children: "".concat(getTimeElapsedInStopWatchFormatString(0)) })), _jsx("span", __assign({ style: {
                                                        fontSize: "1.3",
                                                        marginLeft: "6px",
                                                        marginRight: "2px",
                                                        // marginTop: "2px",
                                                    } }, { children: "/" })), getTimeElapsedInStopWatchFormatString(audioClipDurationInMilliseconds)] }))] }))] }))) : (_jsx(Skeleton, { variant: "rectangular", sx: {
                                borderRadius: "0px",
                                width: "100%",
                                height: "100%",
                            } }))] })) })), _jsx(CardContent, __assign({ sx: __assign({ ":last-child": { paddingBottom: "16px" } }, (titleInEdit && { paddingTop: "2px !important", paddingBottom: "2px !important" })) }, { children: _jsx(Grid, __assign({ container: true }, { children: _jsx(Grid, __assign({ item: true, xs: 12, container: true, justifyContent: "center" }, { children: !loading ? (!titleInEdit ? (_jsx(Tooltip, __assign({ disableHoverListener: Boolean(title && title.length < titleCutOffPoint), placement: "top", arrow: true, title: title ? title : "" }, { children: _jsx(Typography
                            // gutterBottom
                            , __assign({ 
                                // gutterBottom
                                variant: "h5", fontStyle: {
                                    width: "250px",
                                    textAlign: "center",
                                }, sx: __assign({}, (onTitleEdited && { cursor: "text" })), onClick: function () {
                                    if (onTitleEdited) {
                                        setEditedTitleValue(title);
                                        setTitleInEdit(true);
                                    }
                                } }, { children: textCutOffAddElipsis(title, titleCutOffPoint) })) }))) : (_jsxs(Box, __assign({ component: "div", onBlur: function (event) {
                                // if the blur was because of outside focus
                                // currentTarget is the parent element, relatedTarget is the clicked element
                                // this is so clicking the save edit button doesn't lose focus by default
                                if (!event.currentTarget.contains(event.relatedTarget)) {
                                    window.setTimeout(function () {
                                        setTitleInEdit(false);
                                    }, 100);
                                }
                            }, sx: {
                                display: "flex",
                                alignItems: "center",
                                width: "100%",
                            } }, { children: [_jsx(VTTextField, { ref: textFieldRef, value: editedTitleValue, sx: { flex: 1 }, autoFocus: true, onChange: function (text) {
                                        setEditedTitleValue(text);
                                    }, onKeyDown: function (e) {
                                        var keyCodeForEnterKey = 13;
                                        var keyCodeForEscKey = 27;
                                        if (e.keyCode === keyCodeForEnterKey) {
                                            // grab value
                                            onTitleEdited && onTitleEdited(editedTitleValue);
                                            setTitleInEdit(false);
                                        }
                                        if (e.keyCode === keyCodeForEscKey) {
                                            setTitleInEdit(false);
                                        }
                                    } }), _jsx(IconButton, __assign({ sx: { marginLeft: "10px" }, onClick: function (e) {
                                        onTitleEdited && onTitleEdited(editedTitleValue);
                                        setTitleInEdit(false);
                                    } }, { children: _jsx(SaveAsRoundedIcon, {}) }))] })))) : (_jsx(Skeleton, { variant: "text", sx: { width: "75%", fontSize: "1.4em" } })) })) })) }))] }));
});
